import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { RootState, store } from "../../utils/store";
import { Spinner } from "../../utils/Spinner";
import { Button } from "../../components/tailwind";
import { Option } from "../../components/tailwind/dropdown/Dropdown";
import { ImageCarousel } from "./ImageCarousel";
import { useGetProductQuery } from "../../api/productDetailSlice";
import { CustomerFieldEditModal } from "../../customers/customer-fields/CustomerFieldEditModal";
import { MultiFileDrop } from "../../components/FileDrop";
import { Text } from "../../components/Text";
import {
  useEnrichFromFieldsetMutation,
  useEnrichOneMutation,
  useGetProductEnrichViewDataQuery,
  useGetEnrichResultsQuery,
  useUpdateEnrichResultFieldMutation,
  useRevertEnrichResultFieldMutation,
  useUpdateEnrichInputOverrideMutation,
  useSendEnrichToPimMutation,
  EnrichedField,
  EnrichStatus,
} from "../../api/enrichApi";
import { Job } from "../../customers/jobs/types";
import { JobProgressbarHandler } from "../../customers/jobs/JobProgressbarHandler";
import { SSEClient } from "../../api/SSEApi";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../api/djangoToastSlice";
import { validateFileExtension } from "../../utils/files";
import { TaskQueue } from "../../api/taskQueueApi";
import {
  validateFieldset,
  ValidatedFieldsetField,
} from "../../api/fieldsetApi";
import { useCreateCustomerFieldMutation } from "../../api/customerFieldApi";
import { showMessage } from "../../utils/partialUtils";
import { hasExistingPimValue } from "./enrichUtils";
import { EnrichFieldsetSection } from "./EnrichFieldsetSection";
import { EnrichViewHeader } from "./EnrichViewHeader";
import { EnrichFieldTable } from "./EnrichFieldTable";

const ALLOWED_EXTENSIONS = ["pdf"];

type Props = {
  productId: string;
};

export const ProductEnrichView: React.FC<Props> = ({ productId }) => {
  const token = useSelector((state: RootState) => state.auth.token);

  const { data: product, isLoading: isProductLoading } = useGetProductQuery(
    productId
  );
  const {
    data: pageData,
    isLoading: isDataLoading,
    error: dataError,
  } = useGetProductEnrichViewDataQuery(productId);

  const {
    data: enrichResults,
    isLoading: isResultLoading,
    refetch: refetchEnrichResult,
  } = useGetEnrichResultsQuery(productId);

  const [
    enrichFromFieldset,
    { isLoading: isEnriching },
  ] = useEnrichFromFieldsetMutation();
  const [enrichOne] = useEnrichOneMutation();

  const [updateEnrichResultField] = useUpdateEnrichResultFieldMutation();
  const [revertEnrichResultField] = useRevertEnrichResultFieldMutation();

  const [sendToPim] = useSendEnrichToPimMutation();
  const [isSendingToPim, setIsSendingToPim] = useState(false);
  const [
    createCustomerField,
    { isLoading: isCreatingField },
  ] = useCreateCustomerFieldMutation();
  const [enrichingFieldIds, setEnrichingFieldIds] = useState<Set<number>>(
    new Set()
  );
  const taskQueueRef = useRef(new TaskQueue(token ?? ""));

  useEffect(() => {
    taskQueueRef.current.token = token ?? "";
  }, [token]);
  const [
    updateEnrichInputOverride,
    { isLoading: isSavingInputOverride },
  ] = useUpdateEnrichInputOverrideMutation();

  const [selectedFieldset, setSelectedFieldset] = useState<number | null>(null);
  const [allFields, setAllFields] = useState<ValidatedFieldsetField[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [activeJobDetailUrl, setActiveJobDetailUrl] = useState<string | null>(
    null
  );
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);
  const [enrichFieldIds, setEnrichFieldIds] = useState<Set<number>>(
    () => new Set()
  );
  const [enrichingJob, setEnrichingJob] = useState<Job | null>(null);
  const [streamIsActive, setStreamIsActive] = useState(false);
  const [editableWebIdentification, setEditableWebIdentification] = useState<
    Record<string, string>
  >({});
  const [editableSupplierText, setEditableSupplierText] = useState("");

  const isSelectedFileInvalid = useMemo((): boolean => {
    return selectedFiles.some(
      (f) => !validateFileExtension(f, ALLOWED_EXTENSIONS)
    );
  }, [selectedFiles]);

  // Set initial fieldset when pageData loads
  useEffect(() => {
    if (pageData?.initial_fieldset_id && !selectedFieldset) {
      setSelectedFieldset(pageData.initial_fieldset_id);
    }
  }, [pageData?.initial_fieldset_id]);

  const handleAddFiles = (files: File[]): void => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number): void => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };
  // Auto-resume streaming if there's an active job when page loads
  useEffect(() => {
    if (pageData?.job_is_active && !activeJobId) {
      setActiveJobId(pageData.latest_job_id);
      setActiveJobDetailUrl(pageData.latest_job_url ?? null);
    }
  }, [
    pageData?.latest_job_id,
    pageData?.latest_job_url,
    pageData?.job_is_active,
  ]);

  useEffect(() => {
    if (!pageData) {
      return;
    }

    setEditableWebIdentification(
      Object.fromEntries(
        Object.entries(pageData.web_identification).map(([key, value]) => [
          key,
          String(value ?? ""),
        ])
      )
    );
    setEditableSupplierText(pageData.supplier_text || "");
  }, [pageData?.web_identification, pageData?.supplier_text]);

  // SSE stream management
  const startStream = useCallback((): { close: () => void } => {
    if (!activeJobId || !token || streamIsActive) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return { close: (): void => {} };
    }

    const sseClient = new SSEClient<Job>(token);
    setStreamIsActive(true);

    sseClient.connect(
      `/api/_internal/customer/jobs/${activeJobId}/stream/`,
      (data) => {
        setEnrichingJob(data);
      },
      () => {
        // On close: cleanup and refetch latest result
        setStreamIsActive(false);
        setActiveJobId(null);
        setActiveJobDetailUrl(null);
        setEnrichingJob(null);
        refetchEnrichResult();
      },
      (error, msg) => {
        // On error: show notification with link to job detail
        setStreamIsActive(false);
        store.dispatch(
          setDjangoToastOpen({
            appearance: NotificationAppearance.ERROR,
            content: msg || "Something went wrong with the enrichment stream.",
            additionalContent: `Check Job ${activeJobId} for more information.`,
          })
        );
        // Keep activeJobId so user can see it failed
      }
    );

    return {
      close: (): void => {
        sseClient.close();
        setStreamIsActive(false);
      },
    };
  }, [activeJobId, token, streamIsActive, refetchEnrichResult]);

  useEffect(() => {
    if (!activeJobId || !token) {
      return;
    }
    const { close } = startStream();
    return () => {
      close();
    };
  }, [activeJobId, token]);

  const fetchValidation = useCallback(async (): Promise<void> => {
    if (!selectedFieldset || !token) {
      setAllFields([]);
      return;
    }
    setIsValidating(true);
    setValidationError(null);
    try {
      const results = await validateFieldset(
        token,
        selectedFieldset,
        productId
      );
      setAllFields(results);
    } catch (error) {
      console.error("Failed to validate fieldset:", error);
      setValidationError("Failed to validate fieldset");
      setAllFields([]);
    } finally {
      setIsValidating(false);
    }
  }, [selectedFieldset, token, productId]);

  useEffect(() => {
    fetchValidation();
  }, [fetchValidation]);

  const allFieldsValid =
    allFields.length > 0 && allFields.every((f) => f.is_valid_field);

  const sortedValidationFields = useMemo((): ValidatedFieldsetField[] => {
    const rank = (f: ValidatedFieldsetField): number => {
      if (!f.is_valid_field) return 0;
      if (hasExistingPimValue(f)) return 1;
      return 2;
    };
    return [...allFields].sort((a, b) => rank(a) - rank(b));
  }, [allFields]);

  const handleCreateFieldForValidation = async (
    fieldKey: string
  ): Promise<void> => {
    try {
      await createCustomerField({
        field_id: fieldKey,
        name: fieldKey,
      }).unwrap();
      showMessage(
        NotificationAppearance.SUCCESS,
        `Created PIM Field: ${fieldKey}`
      );
      await fetchValidation();
    } catch {
      showMessage(NotificationAppearance.ERROR, "Failed to create field");
    }
  };

  const handleEditModalSave = (): void => {
    setEditingFieldId(null);
    void fetchValidation();
    refetchEnrichResult();
  };

  const showValidationFields =
    !enrichResults?.length ||
    (pageData?.initial_fieldset_id != null &&
      selectedFieldset !== pageData.initial_fieldset_id);

  const enrichFieldSelectionKey = useMemo(() => {
    const keys = sortedValidationFields
      .map((f) => f.key)
      .sort()
      .join("|");
    return `v:${selectedFieldset ?? 0}:${keys}`;
  }, [selectedFieldset, sortedValidationFields]);

  useEffect(() => {
    const next = new Set<number>();
    for (const f of sortedValidationFields) {
      if (
        f.customer_field_id != null &&
        f.is_valid_field &&
        !hasExistingPimValue(f)
      ) {
        next.add(f.customer_field_id);
      }
    }
    setEnrichFieldIds(next);
  }, [enrichFieldSelectionKey]);

  const enrichSelectableFieldIds = useMemo(
    () =>
      sortedValidationFields
        .filter((f) => f.customer_field_id != null && f.is_valid_field)
        .map((f) => f.customer_field_id),
    [sortedValidationFields]
  );

  const {
    enrichSelectAllChecked,
    enrichSelectAllIndeterminate,
  } = useMemo(() => {
    const ids = enrichSelectableFieldIds;
    const n = ids.length;
    if (n === 0) {
      return {
        enrichSelectAllChecked: false,
        enrichSelectAllIndeterminate: false,
      };
    }
    let selected = 0;
    for (const id of ids) {
      if (enrichFieldIds.has(id)) {
        selected += 1;
      }
    }
    return {
      enrichSelectAllChecked: selected === n,
      enrichSelectAllIndeterminate: selected > 0 && selected < n,
    };
  }, [enrichSelectableFieldIds, enrichFieldIds]);

  const hasSelectedFields = enrichFieldIds.size > 0;
  const selectedEnrichResults = useMemo(
    () =>
      (enrichResults ?? []).filter(
        (r) =>
          r.customer_field_id != null && enrichFieldIds.has(r.customer_field_id)
      ),
    [enrichResults, enrichFieldIds]
  );

  const toggleAllEnrichFields = useCallback(() => {
    setEnrichFieldIds((prev) => {
      const ids = sortedValidationFields
        .filter((f) => f.customer_field_id != null && f.is_valid_field)
        .map((f) => f.customer_field_id);
      const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allOn) {
        for (const id of ids) {
          next.delete(id);
        }
      } else {
        for (const id of ids) {
          next.add(id);
        }
      }
      return next;
    });
  }, [sortedValidationFields]);

  const toggleEnrichFieldId = useCallback(
    (customerFieldId: number) => {
      if (!enrichSelectableFieldIds.includes(customerFieldId)) {
        return;
      }
      setEnrichFieldIds((prev) => {
        const n = new Set(prev);
        if (n.has(customerFieldId)) {
          n.delete(customerFieldId);
        } else {
          n.add(customerFieldId);
        }
        return n;
      });
    },
    [enrichSelectableFieldIds]
  );

  const handleEnrich = async (): Promise<void> => {
    if (!selectedFieldset) {
      alert("Please select an Input Schema first");
      return;
    }
    if (isSelectedFileInvalid) {
      return;
    }

    const selectableIds = enrichSelectableFieldIds;
    const customer_field_ids = selectableIds.filter((id) =>
      enrichFieldIds.has(id)
    );
    if (customer_field_ids.length === 0) {
      alert("Select at least one field to enrich");
      return;
    }

    try {
      const selectedFieldsetName =
        pageData?.fieldsets.find(([id]) => id === selectedFieldset)?.[1] ??
        undefined;
      const result = await enrichFromFieldset({
        product_id: productId,
        fieldset_name: selectedFieldsetName,
        add_reasoning: true,
        uploaded_files: selectedFiles,
        customer_field_ids,
      }).unwrap();

      // Start streaming the job progress
      setActiveJobId(result.job_id);
      setActiveJobDetailUrl(result.job_detail_url);
    } catch (error) {
      console.error("Enrichment failed:", error);
      alert("Enrichment failed. Please try again.");
    }
  };

  const handleSaveField = async (
    enrich_result_id: number,
    fieldValue: any
  ): Promise<void> => {
    await updateEnrichResultField({
      product_id: productId,
      enrich_result_id: enrich_result_id,
      field_value: fieldValue,
    }).unwrap();
  };

  const handleRevertField = async (enrich_result_id: number): Promise<void> => {
    await revertEnrichResultField({
      product_id: productId,
      enrich_result_id: enrich_result_id,
    }).unwrap();
  };

  const handleSendToPim = async (fields?: EnrichedField[]): Promise<void> => {
    const toSend = Array.isArray(fields) ? fields : selectedEnrichResults;
    if (toSend.length === 0) {
      return;
    }
    setIsSendingToPim(true);
    try {
      await sendToPim({
        product_id: productId,
        enrich_result_ids: toSend.map((f) => f.id),
      }).unwrap();
    } catch (error) {
      console.error("Failed to send enrich results to PIM:", error);
    } finally {
      setIsSendingToPim(false);
    }
  };

  const handleEnrichField = async (field: EnrichedField): Promise<void> => {
    if (!field.customer_field_id) return;

    try {
      const { task_id } = await enrichOne({
        product_id: productId,
        customer_field_id: field.customer_field_id,
        add_reasoning: true,
        uploaded_files: selectedFiles.length > 0 ? selectedFiles : undefined,
      }).unwrap();

      setEnrichingFieldIds((prev) => new Set(prev).add(field.id));
      taskQueueRef.current.addTaskId(task_id);
      await taskQueueRef.current.waitUntilTaskIsDone(task_id);
      refetchEnrichResult();
    } catch {
      // TaskQueue shows a notification on failure
    } finally {
      setEnrichingFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(field.id);
        return next;
      });
    }
  };

  const getStatusBadgeColor = (status: EnrichStatus): string => {
    switch (status) {
      case EnrichStatus.NOT_ENRICHED:
        return "tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300";
      case EnrichStatus.PARTIALLY_ENRICHED:
        return "tw-bg-amber-100 tw-text-amber-900 tw-border-amber-300";
      case EnrichStatus.ENRICHED:
        return "tw-bg-green-100 tw-text-green-800 tw-border-green-300";
      case EnrichStatus.ENRICHED_AND_SENT_TO_PIM:
        return "tw-bg-blue-100 tw-text-blue-800 tw-border-blue-300";
      case EnrichStatus.FAILED_TO_SEND_TO_PIM:
        return "tw-bg-red-100 tw-text-red-800 tw-border-red-300";
      default:
        return "tw-bg-gray-100 tw-text-gray-800 tw-border-gray-300";
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toISOString().replace("T", " ").slice(0, 19);
  };

  const getFieldsetOptions = (): Option<number>[] => {
    if (!pageData?.fieldsets) return [];
    return pageData.fieldsets.map(([id, name]) => ({
      key: String(id),
      value: id,
      text: name,
    }));
  };

  const hasWebIdentificationChanges = (): boolean => {
    if (!pageData) return false;
    const currentEntries = Object.entries(
      pageData.web_identification
    ).map(([key, value]) => [key, String(value ?? "")]);
    const editableEntries = Object.entries(
      editableWebIdentification
    ).map(([key, value]) => [key, String(value ?? "")]);
    return JSON.stringify(currentEntries) !== JSON.stringify(editableEntries);
  };

  const hasSupplierTextChanges = (): boolean => {
    if (!pageData) return false;
    return (pageData.supplier_text || "") !== editableSupplierText;
  };

  const handleSaveWebIdentification = async (): Promise<void> => {
    try {
      await updateEnrichInputOverride({
        product_id: productId,
        web_identification: editableWebIdentification,
      }).unwrap();
    } catch (error) {
      console.error("Failed to save web identification:", error);
      alert("Failed to save web identification. Please try again.");
    }
  };

  const handleSaveSupplierText = async (): Promise<void> => {
    try {
      await updateEnrichInputOverride({
        product_id: productId,
        supplier_text: editableSupplierText,
      }).unwrap();
    } catch (error) {
      console.error("Failed to save supplier text:", error);
      alert("Failed to save supplier text. Please try again.");
    }
  };

  const sendErrors = (enrichResults?.filter((r) => r.send_error) ?? [])
    .map((r) => `${r.key}: ${r.send_error}`)
    .join("\n");

  if (isProductLoading || isDataLoading || isResultLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (dataError || !pageData) {
    return (
      <div className="tw-p-4 tw-bg-red-100 tw-text-red-700 tw-rounded">
        {dataError ? "Failed to fetch enrich data" : "Failed to load data"}
      </div>
    );
  }

  return (
    <div className="tw-container tw-mx-auto tw-p-6">
      <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4 tw-mb-6 tw-flex tw-justify-between">
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">ID</p>
          <p className="tw-mt-0 tw-pt-0">{product.external_id || product.id}</p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">
            Fieldset
          </p>
          <p className="tw-mt-0 tw-pt-0">{pageData?.product_fieldset}</p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">Created</p>
          <p className="tw-mt-0 tw-pt-0">
            {formatTimestamp(product.created_at)}
          </p>
        </div>
        <div>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0 tw-pb-0">Status</p>
          <span
            className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-border ${getStatusBadgeColor(
              pageData.status
            )} ${
              pageData.status === EnrichStatus.FAILED_TO_SEND_TO_PIM &&
              sendErrors
                ? "tw-cursor-pointer"
                : ""
            }`}
            {...(pageData.status === EnrichStatus.FAILED_TO_SEND_TO_PIM &&
              sendErrors && {
                "data-balloon": `(Click to copy) - ${sendErrors}`,
                "data-balloon-pos": "left",
                "data-balloon-length": "xlarge",
                role: "button",
                tabIndex: 0,
                onClick: (): void => {
                  navigator.clipboard.writeText(sendErrors ?? "");
                  store.dispatch(
                    setDjangoToastOpen({
                      appearance: NotificationAppearance.SUCCESS,
                      content: "Error message copied to clipboard",
                    })
                  );
                },
              })}
          >
            {pageData.status}
          </span>
        </div>
      </div>

      {/* Input for Enrichment Section */}
      <div className="tw-mb-6">
        <p className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0 tw-pb-0">
          Input for Enrichment
        </p>
        <p className="tw-text-gray-500 tw-text-sm tw-mt-0 tw-pt-0">
          The data that will be sent to the AI to use as a basis for the
          enrichment
        </p>
        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
          {/* Web Identification */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Web Identification
            </h3>
            {Object.keys(pageData.web_identification).length > 0 ? (
              <div className="tw-space-y-2">
                {Object.entries(pageData.web_identification).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className="tw-border-b tw-border-gray-200 tw-pb-2 last:tw-border-b-0"
                    >
                      <div className="tw-text-sm tw-font-medium tw-text-gray-600">
                        {key}
                      </div>
                      <input
                        type="text"
                        value={
                          editableWebIdentification[key] ?? String(value ?? "")
                        }
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setEditableWebIdentification((prev) => ({
                            ...prev,
                            [key]: nextValue,
                          }));
                        }}
                        className="tw-w-full tw-text-sm tw-text-gray-900 tw-mt-1 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
                        disabled={!!activeJobId || isSavingInputOverride}
                      />
                    </div>
                  )
                )}
                <div className="tw-flex tw-justify-end tw-mt-3">
                  <Button
                    variant="textual-blue"
                    size="sm"
                    compact="very"
                    onClick={handleSaveWebIdentification}
                    disabled={
                      !hasWebIdentificationChanges() ||
                      !!activeJobId ||
                      isSavingInputOverride
                    }
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <p className="tw-text-gray-500 tw-text-sm">
                No web identification fields configured
              </p>
            )}
          </div>

          {/* Product Images */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Product Images
            </h3>
            {pageData.image_urls.length > 0 ? (
              <ImageCarousel images={pageData.image_urls} />
            ) : (
              <p className="tw-text-gray-500 tw-text-sm">No images available</p>
            )}
          </div>

          {/* Supplier Text */}
          <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-4">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
              Additional information
            </h3>
            <div className="tw-text-sm tw-font-medium tw-text-gray-600">
              {pageData.supplier_text_field_id || "Supplier Text"}
            </div>
            <textarea
              value={editableSupplierText}
              onChange={(event) => setEditableSupplierText(event.target.value)}
              className="tw-w-full tw-text-sm tw-text-gray-900 tw-px-2 tw-py-1 tw-border tw-border-gray-300 tw-rounded"
              placeholder="No supplier text available"
              disabled={!!activeJobId || isSavingInputOverride}
            />
            <div className="tw-flex tw-justify-end tw-mt-3">
              <Button
                variant="textual-blue"
                size="sm"
                compact="very"
                onClick={handleSaveSupplierText}
                disabled={
                  !hasSupplierTextChanges() ||
                  !!activeJobId ||
                  isSavingInputOverride
                }
              >
                Save
              </Button>
            </div>
            <div className="tw-mt-3">
              <h3 className="tw-text-lg tw-font-semibold tw-mb-3 tw-text-gray-900">
                PDF files
              </h3>
              <MultiFileDrop onAddFiles={handleAddFiles} buttonOnly />
              {selectedFiles.length > 0 && (
                <ul className="tw-mt-2 tw-space-y-1 tw-list-none tw-pl-0">
                  {selectedFiles.map((file, index) => {
                    const isInvalid = !validateFileExtension(
                      file,
                      ALLOWED_EXTENSIONS
                    );
                    return (
                      <li
                        key={`${file.name}`}
                        className="tw-flex tw-items-center tw-gap-2 tw-py-1"
                      >
                        <span className="tw-text-sm tw-truncate tw-flex-1">
                          {file.name}
                        </span>
                        {isInvalid && (
                          <Text color="red" className="tw-text-sm">
                            Invalid file type
                          </Text>
                        )}
                        <Button
                          type="button"
                          variant="secondary-alt"
                          size="sm"
                          compact
                          content="Remove"
                          onClick={(): void => handleRemoveFile(index)}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Job in progress — show progress bar */}
      {activeJobId && (
        <div className="tw-mb-6 tw-bg-white tw-shadow tw-rounded-lg">
          <div className="tw-p-6">
            <div className="tw-mb-4">
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
                Enriching...
              </h3>
              <p className="tw-text-sm tw-text-gray-500">
                Job ID: {activeJobId}
                {" - "}
                {activeJobDetailUrl ? (
                  <a
                    href={activeJobDetailUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="tw-text-blue-600 hover:tw-underline"
                  >
                    View details
                  </a>
                ) : null}
                {streamIsActive && (
                  <span className="tw-text-xs tw-text-gray-500 tw-animate-pulse tw-ml-2">
                    Checking Status...
                  </span>
                )}
              </p>
            </div>
            <JobProgressbarHandler
              job={enrichingJob}
              fetchingJob={!enrichingJob}
              retryClicked={false}
            />
          </div>
        </div>
      )}

      {/* Enrich result / validation table */}
      {!activeJobId && (
        <div className="tw-mb-6 tw-bg-white tw-shadow tw-rounded-lg">
          <EnrichFieldsetSection
            fieldsetOptions={getFieldsetOptions()}
            selectedFieldset={selectedFieldset}
            onFieldsetChange={(id) => setSelectedFieldset(id)}
            isValidating={isValidating}
            validationError={validationError}
            disabled={!!activeJobId}
          />
          <EnrichViewHeader
            hasEnrichResults={!!enrichResults?.length}
            onEnrich={handleEnrich}
            isEnriching={isEnriching}
            hasSelectedFields={hasSelectedFields}
            selectedFieldset={selectedFieldset}
            isFileInvalid={isSelectedFileInvalid}
            showValidationFields={showValidationFields}
            allFieldsValid={allFieldsValid}
            onSendToPim={handleSendToPim}
            isSendingToPim={isSendingToPim}
            latestJobUrl={pageData.latest_job_url ?? null}
            hasSelectedSendableFields={selectedEnrichResults.length > 0}
          />
          <EnrichFieldTable
            sortedValidationFields={sortedValidationFields}
            enrichResults={enrichResults}
            enrichingFieldIds={enrichingFieldIds}
            activeJobId={activeJobId}
            enrichFieldIds={enrichFieldIds}
            enrichSelectableFieldIds={enrichSelectableFieldIds}
            onEnrichFieldIdToggle={toggleEnrichFieldId}
            enrichSelectAllChecked={enrichSelectAllChecked}
            enrichSelectAllIndeterminate={enrichSelectAllIndeterminate}
            onToggleAllEnrichFields={toggleAllEnrichFields}
            enrichSelectAllDisabled={
              !!activeJobId || enrichSelectableFieldIds.length === 0
            }
            onSaveField={handleSaveField}
            onRevertField={handleRevertField}
            onEnrichField={handleEnrichField}
            onEditField={setEditingFieldId}
            onCreateField={handleCreateFieldForValidation}
            isCreatingField={isCreatingField}
          />
        </div>
      )}
      {editingFieldId && (
        <CustomerFieldEditModal
          customerFieldId={editingFieldId}
          onClose={() => setEditingFieldId(null)}
          onSave={handleEditModalSave}
        />
      )}
    </div>
  );
};
